import * as THREE from 'three';
import { buildBehaviourScenes, BEHAVIOURS, type BehaviourScenes } from './behaviourThumbScenes';
import type { OrientationPose } from '../utils/orientation';
import type { BehaviourKey } from '../editorShell/BehaviourTiles';

const RENDER_SIZE = 192;
// cos of ~1.8 degrees — below this the tiles would not visibly differ
const VIEW_DIR_EPSILON = 0.9995;

class BehaviourThumbRenderer {
  private renderer: THREE.WebGLRenderer | null = null;
  private scenes: BehaviourScenes | null = null;
  private canvases = new Map<BehaviourKey, HTMLCanvasElement>();
  private painters = new WeakMap<HTMLCanvasElement, CanvasRenderingContext2D>();
  private pending = new Set<BehaviourKey>();
  private viewDir = new THREE.Vector3(0.55, 0.5, 0.9);
  private viewDirUnit = new THREE.Vector3(0.55, 0.5, 0.9).normalize();
  private scratchDir = new THREE.Vector3();
  private offset = new THREE.Vector3();
  private key = '';
  private raf = 0;

  private gl(): THREE.WebGLRenderer | null {
    if (this.renderer) return this.renderer;
    try {
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
      renderer.debug.checkShaderErrors = import.meta.env.DEV;
      renderer.setPixelRatio(1);
      renderer.setSize(RENDER_SIZE, RENDER_SIZE, false);
      renderer.setClearColor(0x000000, 0);
      renderer.localClippingEnabled = true;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer = renderer;
    } catch (e) {
      console.warn('[behaviourThumbs] no context available; tiles stay blank', e);
      return null;
    }
    return this.renderer;
  }

  attach(behaviour: BehaviourKey, canvas: HTMLCanvasElement | null): void {
    if (!canvas) {
      this.canvases.delete(behaviour);
      this.pending.delete(behaviour);
      return;
    }
    if (canvas.width !== RENDER_SIZE) canvas.width = RENDER_SIZE;
    if (canvas.height !== RENDER_SIZE) canvas.height = RENDER_SIZE;
    this.canvases.set(behaviour, canvas);
    this.pending.add(behaviour);
    this.start();
  }

  setSource(key: string, src: THREE.Object3D | null, pose: OrientationPose, billboarded: boolean): void {
    if (!src) return;
    const sourceKey = `${key}|${pose}|${billboarded ? 'bb' : 'free'}`;
    if (sourceKey === this.key && this.scenes) return;
    this.scenes?.dispose();
    try {
      this.scenes = buildBehaviourScenes(src, pose, billboarded);
      this.key = sourceKey;
      for (const behaviour of BEHAVIOURS) this.pending.add(behaviour);
      this.start();
    } catch (e) {
      console.warn('[behaviourThumbs] scenes could not be built', e);
      this.scenes = null;
      this.key = '';
    }
  }

  /** Repaint on camera movement, but never rebuild — only a new source warrants that. */
  setViewDir(v: THREE.Vector3): void {
    if (v.lengthSq() < 1e-8) return;
    this.viewDir.copy(v);

    this.scratchDir.copy(v).normalize();
    if (this.scratchDir.dot(this.viewDirUnit) > VIEW_DIR_EPSILON) return;

    this.viewDirUnit.copy(this.scratchDir);
    this.repaintCameraDependent();
  }

  /** A fixedDir view looks identical from every angle, so camera moves must skip it. */
  private repaintCameraDependent(): void {
    const scenes = this.scenes;
    if (!scenes) return;

    let queued = false;
    for (const behaviour of this.canvases.keys()) {
      if (scenes.views[behaviour]?.fixedDir) continue;
      this.pending.add(behaviour);
      queued = true;
    }
    if (queued) this.start();
  }

  clear(): void {
    this.stop();
    this.pending.clear();
    this.scenes?.dispose();
    this.scenes = null;
    this.key = '';
    for (const canvas of this.canvases.values()) {
      this.painter(canvas)?.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  private painter(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
    const existing = this.painters.get(canvas);
    if (existing) return existing;
    const ctx = canvas.getContext('2d');
    if (ctx) this.painters.set(canvas, ctx);
    return ctx;
  }

  private start(): void {
    if (this.raf || this.pending.size === 0) return;
    this.raf = requestAnimationFrame(this.tick);
  }

  private stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private tick = (): void => {
    this.raf = 0;
    if (!this.scenes || this.pending.size === 0) return;
    const gl = this.gl();
    if (!gl) {
      this.pending.clear();
      return;
    }

    const due = Array.from(this.pending);
    this.pending.clear();

    for (const behaviour of due) {
      const view = this.scenes.views[behaviour];
      const canvas = this.canvases.get(behaviour);
      const ctx = canvas ? this.painter(canvas) : null;
      if (!view || !canvas || !ctx) continue;

      try {
        const dir = view.fixedDir ?? this.viewDir;
        if (!view.fixedDir) view.relight?.(this.viewDir);
        this.offset.copy(dir).normalize().multiplyScalar(this.scenes.dist);

        const camera = this.scenes.camera;
        camera.aspect = 1;
        camera.updateProjectionMatrix();
        camera.position.copy(view.target).add(this.offset);
        camera.lookAt(view.target);

        gl.render(view.scene, camera);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(gl.domElement, 0, 0, canvas.width, canvas.height);
      } catch (e) {
        console.warn('[behaviourThumbs] render failed', e);
      }
    }
  };
}

export const behaviourThumbRenderer = new BehaviourThumbRenderer();
